/**
 * Dialog/Modal Component
 *
 * Simple modal dialog with backdrop and close on escape/outside click
 */

import { useEffect, useCallback, useRef, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from './Button'

// =============================================================================
// Types
// =============================================================================

interface DialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
}

interface DialogContentProps {
  className?: string
  children: ReactNode
  title?: string
  description?: string
}

// =============================================================================
// Dialog Root
// =============================================================================

/** Lo que puede recibir el foco dentro del dialogo. */
const ENFOCABLES = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

export function Dialog({ open, onOpenChange, children }: DialogProps) {
  const contenedor = useRef<HTMLDivElement>(null)
  /** Quien tenia el foco antes de abrir, para devolverselo al cerrar. */
  const focoPrevio = useRef<HTMLElement | null>(null)

  /**
   * El aviso de cierre se guarda en una referencia.
   *
   * Los padres lo pasan como funcion en linea, asi que cambia de identidad en
   * cada render. Si el efecto que coloca el foco dependiera de el, se repetiria
   * con cada tecla: devolveria el foco y volveria a ponerlo en el primer
   * elemento del dialogo, que es la equis. Escribir se volvia imposible.
   */
  const alCambiarAbierto = useRef(onOpenChange)
  alCambiarAbierto.current = onOpenChange

  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      alCambiarAbierto.current(false)
    }
  }, [])

  /**
   * El tabulador no debe escaparse del dialogo.
   *
   * Un dialogo modal dice, con `aria-modal`, que el resto de la pagina no
   * existe mientras esta abierto. Si el tabulador sale de el, quien navega
   * por teclado acaba rellenando campos del formulario de detras sin verlos:
   * la promesa del atributo y el comportamiento real se contradicen.
   */
  const atraparTabulador = useCallback((e: KeyboardEvent) => {
    if (e.key !== 'Tab' || !contenedor.current) return

    const elementos = Array.from(
      contenedor.current.querySelectorAll<HTMLElement>(ENFOCABLES)
    ).filter((el) => el.offsetParent !== null)

    if (elementos.length === 0) {
      e.preventDefault()
      return
    }

    const primero = elementos[0]!
    const ultimo = elementos[elementos.length - 1]!
    const activo = document.activeElement

    // Al llegar al final se vuelve al principio, y al reves con mayusculas.
    if (!e.shiftKey && activo === ultimo) {
      e.preventDefault()
      primero.focus()
    } else if (e.shiftKey && activo === primero) {
      e.preventDefault()
      ultimo.focus()
    } else if (!contenedor.current.contains(activo)) {
      e.preventDefault()
      primero.focus()
    }
  }, [])

  useEffect(() => {
    if (open) {
      focoPrevio.current = document.activeElement as HTMLElement | null

      document.addEventListener('keydown', handleEscape)
      document.addEventListener('keydown', atraparTabulador, true)
      document.body.style.overflow = 'hidden'

      /**
       * El foco entra al dialogo al abrirlo.
       *
       * Sin esto queda donde estaba, detras del velo: quien usa teclado o
       * lector de pantalla tiene que tabular a ciegas hasta encontrarlo, y
       * quien usa lector no se entera siquiera de que se abrio algo.
       *
       * Se elige el primer campo y no el boton de cerrar: lo primero que
       * quiere hacer quien abre un formulario es escribir en el.
       */
      const elementos = Array.from(
        contenedor.current?.querySelectorAll<HTMLElement>(ENFOCABLES) ?? []
      ).filter((el) => el.offsetParent !== null)

      // Sin retardo: este efecto ya corre con el contenido montado. Un
      // `setTimeout` aqui deja una ventana en la que el foco sigue detras
      // del velo, y ademas hace que el comportamiento dependa de cuanto
      // tarde el equipo.
      if (elementos.length > 0) {
        elementos[0]!.focus()
      } else if (contenedor.current) {
        contenedor.current.tabIndex = -1
        contenedor.current.focus()
      }

      return () => {
        document.removeEventListener('keydown', handleEscape)
        document.removeEventListener('keydown', atraparTabulador, true)
        document.body.style.overflow = ''

        // Y al cerrar vuelve a donde estaba. Sin esto el foco cae al
        // principio del documento y hay que recorrer la pagina entera para
        // volver al boton que se acaba de usar.
        focoPrevio.current?.focus?.()
      }
    }

    return undefined
  }, [open, handleEscape, atraparTabulador])

  if (!open) return null

  return (
    <div ref={contenedor} className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />
      {/* Content container */}
      {children}
    </div>
  )
}

// =============================================================================
// Dialog Content
// =============================================================================

export function DialogContent({
  className,
  children,
  title,
  description,
}: DialogContentProps) {
  return (
    <div
      className={cn(
        'relative z-50 w-full max-w-lg rounded-lg bg-[var(--background)] p-6 shadow-lg',
        'animate-in fade-in-0 zoom-in-95 duration-200',
        className
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'dialog-title' : undefined}
      aria-describedby={description ? 'dialog-description' : undefined}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  )
}

// =============================================================================
// Dialog Header
// =============================================================================

export function DialogHeader({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  return (
    <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)}>
      {children}
    </div>
  )
}

// =============================================================================
// Dialog Title
// =============================================================================

export function DialogTitle({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  return (
    <h2
      id="dialog-title"
      className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    >
      {children}
    </h2>
  )
}

// =============================================================================
// Dialog Description
// =============================================================================

export function DialogDescription({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  return (
    <p
      id="dialog-description"
      className={cn('text-sm text-[var(--foreground-muted)]', className)}
    >
      {children}
    </p>
  )
}

// =============================================================================
// Dialog Footer
// =============================================================================

export function DialogFooter({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  return (
    <div
      className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    >
      {children}
    </div>
  )
}

// =============================================================================
// Dialog Close Button
// =============================================================================

export function DialogClose({
  onClose,
  className,
}: {
  onClose: () => void
  className?: string
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn('absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100', className)}
      onClick={onClose}
    >
      <X className="h-4 w-4" />
      <span className="sr-only">Cerrar</span>
    </Button>
  )
}
